import type { EventService } from './event-service';
import type { I18nService } from '../../i18n/i18n.service';
import { UserProfile } from '../model/user-profile';
import { HttpService } from './http-service';
import { HostUrlHelper } from './host-url-helper';
import { EventCodes } from '../model/event-codes';

export class UserProfileService {
	static readonly $inject = ['mx.shell.EventService', 'mx.shell.InternationalizationService', 'mx.SolutionBuilderAgent.Http', 'mx.shell.HostUrlHelper'];
	$inject = ['$timeout', 'mx.SolutionBuilderAgent.Http', 'mx.shell.Config', 'mx.shell.DataStorage', 'mx.shell.UserProfile', 'mx.shell.HostUrlHelper'];

	userInfo?: UserProfile;

	get id() {
		return this.userInfo?.id;
	}

	constructor(
		private readonly eventService: EventService,
		private readonly i18nService: I18nService,
		private readonly http: HttpService,
		private readonly hostUrlHelper: HostUrlHelper
	) {}

	private save(profile: UserProfile) {
		return this.http.post('api/userinfo', profile).then(function () {
			setTimeout(() => location.reload(), 500);
		});
	}

	private async load() {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const data = (await this.http.get('api/userinfo', undefined, undefined, true)) as any;

		const avatarUrl = this.hostUrlHelper.getFullImageUrl(data.Photo);
		const userInfo = new UserProfile(
			data.Id,
			data.FirstName,
			data.LastName,
			data.LastName + (data.FirstName ? ', ' + data.FirstName : ''), // TODO to use correct account name
			avatarUrl,
			data.Email,
			data.Phone,
			data.LanguageId,
			data.IsDevMode,
			data.IsCustomizationMode,
			data.IsAdmin,
			data.ObjectID,
			data.State,
			data.ProfileDialogId,
			data.InvariantRoleNames || [],
			data.Currency,
			data.NotificationSoundPath,
			data.UserType,
			data.CreatedDate,
			data.LastActivityDate
		);

		const language = (await this.i18nService.getLanguageById(userInfo.languageId)) || (await this.i18nService.getDefaultUiLanguage());
		userInfo.languageId = language.id;
		userInfo.language = language;
		return userInfo;
	}

	async get(reload = false) {
		if (!reload && this.userInfo) {
			return this.userInfo;
		}

		const userInfo = await this.load();
		this.set(userInfo);
		return userInfo;
	}

	set(userInfo: UserProfile) {
		this.userInfo = userInfo;
		this.eventService.dispatchEvent(EventCodes.ProfileChanged, userInfo);
	}

	async changeLanguage(languageId: string) {
		const userInfo = await this.get();
		userInfo.languageId = languageId;
		this.set(userInfo);
		await this.save(userInfo);
	}

	async isCustomizationMode() {
		const profile = await this.get();
		return profile.isCustomizationMode || profile.isDevMode;
	}

	async isDevMode() {
		const profile = await this.get();
		return profile.isDevMode;
	}

	async isAdminMode() {
		const profile = await this.get();
		return Boolean(profile.isAdmin || profile.isDevMode || profile.isCustomizationMode);
	}
}
